import {Middleware} from '@reduxjs/toolkit'
import {getLogger} from '../../services/logging'
import {setAppView} from '../slices/appViewSlice'
import {
  addItem,
  removeItem,
  showCart,
  updateAllItems,
  updateItem
} from '../slices/shoppingCartSlice'
import {AppView, BookingStatusValues, PaymentStatusValues} from '../../schema'
import {setGuestInfo} from '../slices/guestInfoSlice'
import {setBookingId, setBookingStatus} from '../slices/bookingSlice'
import {setPaymentSessionId, setPaymentStatus} from '../slices/paymentSlice'
import {setConfig} from '../slices/configSlice'
import {setUser} from '../slices/userSlice'
import {submitSearch} from '../slices/searchSubmittedSlice'
import {setSessionId} from '../slices/sessionSlice'
import {
  syncAddToCart,
  syncBeginCheckout,
  syncBooking,
  syncInit,
  syncRemoveFromCart,
  syncIbeSessionId,
  syncSetAppView,
  syncSetUser,
  syncShoppingCart,
  syncShowCart,
  syncSubmitSearch,
  syncUpdateCartItem,
  syncViewSearchResults
} from '../../services/dataLayer'
import {
  ClarityEvent,
  sendClarityEvent,
  setIbeSessionId
} from '../../services/clarity'

const logger = getLogger('analyticsMiddleware')

function routeAppViewAction(appView: AppView): void {
  syncSetAppView(appView)

  switch (appView) {
    case AppView.CHOOSE_PROPERTY:
    case AppView.CHOOSE_UNIT:
      syncViewSearchResults()
    case AppView.CHECKOUT:
      syncBeginCheckout()
      sendClarityEvent(ClarityEvent.Checkout)
      break
    default:
      logger.debug({
        message: 'App view action does not require analytics routing',
        data: {appView}
      })
      break
  }
}

/**
 * Middleware to distribute analytics to various backends like Microsoft Clarity, Google Analytics, etc.
 * @param storeApi The redux store API
 * @returns
 */
export const analyticsMiddleware: Middleware = storeApi => next => action => {
  try {
    const result = next(action)
    const state = storeApi.getState()

    logger.debug({
      message: 'Starting analytics middleware...',
      data: {actionType: action.type}
    })

    switch (action.type) {
      case 'persist/REHYDRATE':
        const ibeSessionId = action.payload?.session?.sessionId
        if (ibeSessionId) {
          syncIbeSessionId(ibeSessionId)
          setIbeSessionId(ibeSessionId)
        }
        break
      case addItem.type:
        syncAddToCart(action)
        syncShoppingCart(state)
        sendClarityEvent(ClarityEvent.AddToCart)
        break
      case removeItem.type:
        syncRemoveFromCart(state)
        syncShoppingCart(state)
        sendClarityEvent(ClarityEvent.RemoveFromCart)
        break
      case setAppView.type:
        routeAppViewAction(action.payload)
        break
      case setBookingId.type:
        sendClarityEvent(ClarityEvent.CompleteBooking)
        syncBooking(action)
        break
      case setBookingStatus.type:
        if (action.payload === BookingStatusValues.Failure) {
          sendClarityEvent(ClarityEvent.BookingFailed)
        }
        break
      case setConfig.type:
        syncInit()
        break
      case setGuestInfo.type:
        sendClarityEvent(ClarityEvent.CompleteGuestInfo)
        break
      case setPaymentSessionId.type:
        sendClarityEvent(ClarityEvent.CompletePayment)
        break
      case setPaymentStatus.type:
        if (action.payload === PaymentStatusValues.Failure) {
          sendClarityEvent(ClarityEvent.PaymentFailed)
        }
        break
      case setSessionId.type:
        syncIbeSessionId(action.payload)
        setIbeSessionId(action.payload)
        break
      case setUser.type:
        syncSetUser(action.payload)
        break
      case showCart.type:
        syncShowCart()
        break
      case submitSearch.type:
        syncSubmitSearch(action.payload)
        break
      case updateAllItems.type:
        syncShoppingCart(state)
        break
      case updateItem.type:
        syncUpdateCartItem(action)
        syncShoppingCart(state)
        break
      default:
        logger.debug({
          message: 'Action does not require analytics routing',
          data: {actionType: action.type}
        })
    }

    return result
  } catch (e) {
    logger.error({
      message: 'Error in analytics middleware',
      errorData: {
        error: e.message
      }
    })
  }
}
